import { Layout, Splitter } from "antd";
import { Content, Header } from "antd/es/layout/layout";
import React, { FC, ReactNode, Suspense, useEffect, useRef } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router';
import './App.less';
import { useAppGlobStore } from './appGlobalStore';
import { XmSpinner } from './component/spinner/XmSpinner';
import { ChatIcon } from './content/aiChat/AIChat';
import { BackendUnavailable } from './content/errorBoundary/BackendUnavailable';
import ErrorBoundary from './content/errorBoundary/ErrorBoundary';
import { ErrorRoutePage } from './content/errorBoundary/ErrorPage';
import HealthStatusDrawer from './content/globalHealthStatus/HealthStatusDrawer';
import { Login } from './content/login/Login';
import { Logout } from './content/logout/Logout';
import MenuFavoritesDrawer from './content/menuFavorite/MenuFavoritesDrawer';
import { ChangeLogModal } from './content/releaseInfo/ChangeLog';
import { ALERTING_SECTION_DEFAULT } from './content/tools/alerting/alertingMenu';
import { CHANGE_TRACKER_SECTION_DEFAULT } from './content/tools/confChanges/changeTrackerMenu';
import { EXPORTER_SECTION_DEFAULT } from './content/tools/exporter/exporterMenu';
import { REPORTER_SECTION_DEFAULT } from './content/tools/reporter/reporterMenu';
import { RoutePath } from './data/Routes';
import { DashboardAutoplay } from './boxParts/dashboard/autoplay/DashboardAutoplay';
import { useWebSocketGrafana, useWebSocketLLM, useWebSocketReload } from './hook/useWebSocketReload';
import LeftMenuDesktop from "./menu/left/LeftMenuDesktop";
import LeftMenuMobile from './menu/left/Mobile/LeftMenuMobile';
import { reloadPage } from './util/reloadPage';
import TopNavBar from './menu/top/TopNavBar';
import { RouteAlerting } from './routing/RouteAlerting';
import { RouteChangeTracker } from './routing/RouteChangeTracker';
import { RouteClassTypeItem } from './routing/RouteClassTypeItem';
import { RouteCmp } from './routing/RouteCmp';
import { RouteDevice } from './routing/RouteDevice';
import { RouteExporter } from './routing/RouteExporter';
import { RouteGroup } from './routing/RouteGroup';
import { RouteReporter } from './routing/RouteReporter';
import { RouteSectionCmp } from './routing/RouteSectionCmp';
import { RouteSettingsHelp } from './routing/RouteSettingsHelp';
import { RouteType } from './routing/RouteType';
import { DataService } from './service/data.service';
import { Log } from './service/log.service';
import { DomUtil } from './util/DomUtil';
import { GLOB } from './util/Glob';

export interface AppState {
  /**
   * @deprecated avoid, TODO: replace
   */
  refresh: boolean;
  isHsOpen: boolean;
  isFavoritesOpen: boolean;
}

interface AppStateFull extends AppState {
  entryElement: ReactNode;
  leftTreeWidth: number;
  isFreeEdition: boolean;
  ready: boolean;
}

const KEY_LEFT_TREE_WIDTH = "xm-left-tree-width";
const IS_MOBILE_THRESHOLD = 768; // MD

// required for #1250 - keep focus on modal even when element outside modal tree is clicked so close by ESC works
// document.querySelector('body').addEventListener('DOMActivate', ev =>
//   !(ev.target as HTMLElement).closest('.ant-modal,.ant-picker-dropdown,.ant-input') && setTimeout(() => {
//     document.querySelector<HTMLElement>('.ant-modal-wrap')?.focus();
//   }));

class App extends React.Component<unknown, AppStateFull> {
  private unsubscribeFreeEdition: () => void = () => undefined;
  private unsubscribeLeftTreeWidth: () => void = () => undefined;
  private unsubscribeShowMenu: () => void = () => undefined;

  private readonly handleFreeEditionChange = (isFreeEdition: boolean): void => {
    this.setState({ isFreeEdition });
  };

  constructor(props) {
    super(props);
    this.state = {
      refresh: false,
      ready: false,
      isHsOpen: false,
      isFavoritesOpen: false,
      entryElement: null,
      leftTreeWidth: useAppGlobStore.getState().leftTreeWidth,
      isFreeEdition: useAppGlobStore.getState().info?.backend?.free ?? false,
    };
    GLOB.getAppState = () => this.state;
    GLOB.setAppState = this.setState.bind(this);
    this.handleInitialLoad();
  }

  componentDidMount(): void {
    this.unsubscribeFreeEdition = useAppGlobStore.subscribe(
      (state) => state.info?.backend?.free ?? false,
      this.handleFreeEditionChange
    );
    this.unsubscribeLeftTreeWidth = useAppGlobStore.subscribe(state => state.leftTreeWidth, width => this.setState({ leftTreeWidth: width }));
    this.unsubscribeShowMenu = useAppGlobStore.subscribe(state => state.showMenu, () => GLOB.refresh());
    //void DataService.checkVersionReload();
    GLOB.xormonReady.promise.then(() => {
      useAppGlobStore.setState({ licenseStatus: DataService.detectLicenseColor() });
    }, console.debug);
    window.addEventListener("resize", this.handleResize);
  }

  handleInitialLoad = () => {
    if (window.innerWidth < IS_MOBILE_THRESHOLD) {
      useAppGlobStore.setState({ isMobile: true });
    }
  };

  handleResize = () => {
    // Setting isMobile state
    if (window.innerWidth < IS_MOBILE_THRESHOLD) {
      useAppGlobStore.setState({ isMobile: true });
      useAppGlobStore.getState().closeDesktopLeftTree();
    } else {
      useAppGlobStore.setState({ isMobile: false });
      useAppGlobStore.getState().openDesktopLeftTree();
    }
  };

  componentWillUnmount(): void {
    this.unsubscribeFreeEdition();
    this.unsubscribeLeftTreeWidth();
    this.unsubscribeShowMenu();
    window.removeEventListener("resize", this.handleResize);
  }

  render() {
    if (GLOB.authService.startupError) {
      return <BackendUnavailable error={GLOB.authService.startupError} />;
    }
    if (!GLOB.authService.isLoggedIn()) {
      return <Login />;
    }

    const showMenu = useAppGlobStore.getState().showMenu;
    const isMobile = useAppGlobStore.getState().isMobile;

    return (
      <BrowserRouter basename={GLOB.getPublicUrl()} useTransitions={false}>
        <Hooks getState={() => this.state} setState={this.setState.bind(this)} />
        {isMobile && <LeftMenuMobile />}

        <Layout>
          <Content>
            <Splitter
              className={!showMenu || isMobile ? 'has-splitter-bar-collapsed' : ''}
              onResize={width => useAppGlobStore.setState({ leftTreeWidth: width[0] })}
              onResizeEnd={width => localStorage.setItem(KEY_LEFT_TREE_WIDTH, String(width[0]))}>
              <Splitter.Panel
                className="left-panel"
                size={this.state.leftTreeWidth}
                defaultSize={this.state.leftTreeWidth}
                min={150}
                max="50%">
                <LeftMenuDesktop />
              </Splitter.Panel>
              <Splitter.Panel className={`right-panel${showMenu ? '' : ' xm-menu-hidden'}`}>
                <Header inert={!showMenu}>
                  <TopNavBar />
                </Header>
                {/* the TV mode fullscreen element - everything but the top and left chrome */}
                <div id={DomUtil.TV_ROOT_ID}>
                  {/* outside the Header - stays visible when a dashboard hides the chrome */}
                  <DashboardAutoplay />
                  <Content>

                    <HealthStatusDrawer key={`${this.state.isHsOpen}-state`} />
                    <MenuFavoritesDrawer key={`${this.state.isFavoritesOpen}-favorites`} />
                    {this.state.ready ? <div id={DomUtil.MAIN_SCROLL_ID} data-testid="main-content" ref={useAppGlobStore.getState().mainScrollRef}>
                      <ErrorBoundary>
                        <Routes>
                          <Route errorElement={<ErrorRoutePage />}>
                            <Route path="/" element={<Suspense fallback={<XmSpinner />}>{this.state.entryElement}</Suspense>} />
                            <Route path={RoutePath.RELOAD} element={<Outlet />} />
                            <Route path={RoutePath.LOGOUT} element={<Logout />} />
                            <Route path={RoutePath.MATCH_CMP} element={<RouteCmp />} />
                            <Route path={RoutePath.ALERTING} element={<Navigate to={ALERTING_SECTION_DEFAULT} replace />} />
                            <Route path={RoutePath.MATCH_ALERTING} element={<RouteAlerting />} />
                            <Route path={RoutePath.EXPORTER} element={<Navigate to={EXPORTER_SECTION_DEFAULT} replace />} />
                            <Route path={RoutePath.MATCH_EXPORTER} element={<RouteExporter />} />
                            <Route path={RoutePath.REPORTER} element={<Navigate to={REPORTER_SECTION_DEFAULT} replace />} />
                            <Route path={RoutePath.MATCH_REPORTER} element={<RouteReporter />} />
                            <Route path={RoutePath.EVENTS} element={<Navigate to={CHANGE_TRACKER_SECTION_DEFAULT} replace />} />
                            <Route path={RoutePath.MATCH_EVENTS} element={<RouteChangeTracker />} />
                            <Route path={RoutePath.MATCH_SECTION_CMP} element={<RouteSectionCmp />} />
                            <Route path={RoutePath.MATCH_SETTINGS_HELP} >
                              <Route index element={<RouteSettingsHelp match={RoutePath.MATCH_SETTINGS_HELP} />} />
                              <Route path={RoutePath.MATCH_GROUP_ONLY} >
                                <Route index element={<RouteSettingsHelp match={`${RoutePath.MATCH_SETTINGS_HELP}/${RoutePath.MATCH_GROUP_ONLY}`} />} />
                                <Route path={RoutePath.MATCH_ITEM_ONLY} element={<RouteSettingsHelp match={`${RoutePath.MATCH_SETTINGS_HELP}/${RoutePath.MATCH_GROUP_ONLY}/${RoutePath.MATCH_ITEM_ONLY}`} />} />
                              </Route>
                            </Route>
                            <Route path={RoutePath.MATCH_DEVICE} element={<RouteDevice />} />
                            <Route path={RoutePath.MATCH_TYPE} element={<RouteType />} />
                            <Route path={RoutePath.MATCH_GROUP_TOP} >
                              <Route index element={<RouteGroup />} />
                              <Route path={RoutePath.MATCH_ITEM_ONLY} element={<RouteGroup />} />
                            </Route>
                            <Route path={RoutePath.MATCH_CLASS_TYPE_ITEM} element={<RouteClassTypeItem />} />
                            <Route path={RoutePath.MATCH_SPLAT} element={<Suspense fallback={<XmSpinner />}>{this.state.entryElement}</Suspense>} />
                          </Route>
                        </Routes>
                      </ErrorBoundary>
                    </div>
                      : <XmSpinner />}
                  </Content>
                </div>
              </Splitter.Panel>
            </Splitter>
          </Content>
        </Layout>
      </BrowserRouter>
    );
  }
}

const Hooks: FC<{ getState: () => AppStateFull, setState: (state: Partial<AppStateFull>) => void; }> = ({ getState, setState, ...props }) => {
  GLOB.grafanaActive = useWebSocketGrafana();
  // Xbot needs an LLM set up in Settings > AI: without one the icon only promotes it, once, until it has been seen
  const llmActive = useWebSocketLLM();
  const dashReload = useWebSocketReload('dashboard');
  const sessionReload = useWebSocketReload('session');
  const changeLogModalTimestamp = useAppGlobStore(state => state.changeLogModalTimestamp);
  const dashReloading = useRef(false);

  useEffect(() => {
    // a rejected start-up ends on the login or backend-unavailable page, nothing to render here
    GLOB.xormonReady.promise.then(() => {
      setState({ ready: true, entryElement: <Navigate to={GLOB.getDefaultPath()} replace /> });
    }, console.debug);
    return () => {
      setState({ ready: false, entryElement: null });
    };
  }, []);

  useEffect(() => {
    // UpgradeReloadPage owns the reload during an upgrade, a logout now would drop its modal
    if (!sessionReload || GLOB.isUpgrading) return;
    if (GLOB.isProdEnv()) {
      Log.warn('Server shutdown. You will be logged out in 5s.');
      setTimeout(() => {
        GLOB.authService.logOut();
      }, 5000);
    }
  }, [sessionReload]);

  useEffect(() => {
    if (!dashReload) return;
    // a resync is our own catch-up after a reconnect: refresh quietly, nobody changed anything
    const resync = dashReload.data.resync;
    // one change can arrive as a burst of messages, warn and reload once for all of them
    if (!resync) {
      if (dashReloading.current) return;
      dashReloading.current = true;
    }
    void GLOB.menuService.reloadDashboards().then(() => {
      const warnMsg = 'Dashboards were changed by another user, reloading...';
      if (GLOB.selectedClass === RoutePath.DASHBOARD) {
        if (!resync)
          Log.warn(warnMsg);
        DataService.reloadLeftMenu?.();
      }
      else if (GLOB.selectedClass === RoutePath.SETTINGS && GLOB.selectedType === RoutePath.CONFIGURATION && GLOB.selectedItem?.path === RoutePath.SET_DASH_MANAGE) {
        // a full page reload on every wake would be disproportionate
        if (resync) return;
        Log.warn(warnMsg);
        reloadPage();
      }
    }).finally(() => { if (!resync) dashReloading.current = false; });
  }, [dashReload]);

  return <>
    {getState().entryElement && <ChangeLogModal timestamp={changeLogModalTimestamp} />}
    {llmActive === true && <ChatIcon />}
    {llmActive === false && !GLOB.userInfo?.configuration?.aiShown && <ChatIcon promo={true} />}
  </>;
};

export default App;
